import type {MaskitoOptions} from "@maskito/core";
import {maskitoNumberOptionsGenerator} from "@maskito/kit";
import {useMaskito} from "@maskito/react";
import {TextField, type TextFieldProps} from "@mui/material";
import type React from "react";
import {useState} from "react";
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    useController,
} from "react-hook-form";

export type RhNumericTextFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    // biome-ignore lint/suspicious/noExplicitAny: defined by RHF
    TContext = any,
    TTransformedValues = TFieldValues,
> = Omit<TextFieldProps, "error" | "onChange" | "onBlur" | "value" | "inputRef"> & {
    control: Control<TFieldValues, TContext, TTransformedValues>;
    name: TName;
};

const priceMask: MaskitoOptions = maskitoNumberOptionsGenerator({
    maximumFractionDigits: 2,
    thousandSeparator: "",
});

const RhfNumericTextField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    control,
    name,
    label,
    ...props
}: RhNumericTextFieldProps<TFieldValues, TName>): React.ReactElement => {
    const inputRef = useMaskito({
        options: priceMask,
    });
    const {field, fieldState} = useController({control, name});
    const [focused, setFocused] = useState(false);
    const fieldHasValue = field.value !== undefined && field.value !== "";

    return (
        <Controller
            render={() => (
                <TextField
                    inputRef={inputRef}
                    name={name}
                    label={label}
                    onBlur={() => {
                        setFocused(false);
                        field.onBlur();
                    }}
                    onFocus={() => {
                        setFocused(true);
                    }}
                    onInput={field.onChange}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ?? ""}
                    {...props}
                    slotProps={{
                        ...props.slotProps,
                        inputLabel: {
                            shrink: fieldHasValue || focused,
                            ...props.slotProps?.inputLabel,
                        },
                    }}
                    sx={{
                        "& .MuiInputLabel-outlined": {
                            ml: fieldHasValue ? 0 : 2,
                            "&.Mui-focused": {
                                ml: 0,
                            },
                        },
                        width: "100%",
                        ...props.sx,
                    }}
                    value={field.value}
                />
            )}
            name={name}
            control={control}
        />
    );
};

export default RhfNumericTextField;
